import React, {useEffect, useRef} from 'react';

import {SkeletonRow} from 'client/TeacherDashboard/components/ListRowsSkeleton';

interface Props {
  /** More rows exist beyond the ones rendered. */
  hasNextPage: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

export const InfiniteScrollSentinel = ({hasNextPage, isLoadingMore, onLoadMore}: Props) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Read through refs so the observer is created once rather than re-subscribing per page. Kept
  // fresh in an effect, not during render — this directory compiles in React Compiler infer mode.
  const canLoadMoreRef = useRef(hasNextPage && !isLoadingMore);
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    canLoadMoreRef.current = hasNextPage && !isLoadingMore;
    onLoadMoreRef.current = onLoadMore;
  });

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[entries.length - 1]?.isIntersecting && canLoadMoreRef.current) {
          onLoadMoreRef.current();
        }
      },
      {rootMargin: '0px 0px 200px 0px', threshold: 0}
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The node is always rendered, never conditionally: the observer subscribes once to whatever
  // `sentinelRef` holds at mount, so unmounting this div when `hasNextPage` goes false would leave
  // the observer watching a detached node and paging would never resume once it came back.
  // `canLoadMoreRef` does the gating instead.
  return (
    <div ref={sentinelRef}>
      {hasNextPage && isLoadingMore && (
        <>
          {/* The Skeleton atom is aria-hidden, so the load needs announcing separately. */}
          <span className='sr-only' role='status'>
            Loading more skill paths…
          </span>
          <SkeletonRow />
          <SkeletonRow />
        </>
      )}
    </div>
  );
};
